import {
  createRouter as createAsahiRouter,
  createWebHistory,
  Router,
  RouteRecordRaw,
  NavigationGuardNext
} from 'vue-router'
import { Store } from 'vuex'
import { IRootState } from '@/store'
import { AuthActionTypes } from '@/store/modules/auth/interfaces/action-types'
import { constants } from '@/constants'
import { getValueByLabel } from '@/utils/permissions'
import DefaultLayout from '@/components/layout/DefaultLayout.vue'
import HomeLayout from '@/components/layout/HomeLayout.vue'

const history = createWebHistory()

let gigyaInitialized = false
let currentGigyaMode: 'default' | 'clp' | null = null

export type AsahiRouter = Router & { navigate: (url: string | CustomEvent) => void }

async function checkSession (store: Store<IRootState>, next: NavigationGuardNext) {
  // Ensure Gigya is fully initialized before checking the session
  await new Promise<void>((resolve) => {
    if (!window.gigya || typeof window.gigya.init !== 'function') {
      // Polling to check if Gigya is initialized
      const checkGigyaInterval = setInterval(() => {
        if (window.gigya && typeof window.gigya.init === 'function') {
          clearInterval(checkGigyaInterval)
          resolve()
        }
      }, 100)

      // Timeout if Gigya is not initialized within a reasonable time
      setTimeout(() => {
        clearInterval(checkGigyaInterval)
        console.error('Gigya is not initialized within the expected time frame.')
        resolve() // Proceed even if initialization fails
      }, 100)
    } else {
      resolve()
    }
  })

  // Now you can safely call window.gigya.hasSession() and other methods
  if (window.gigya && typeof window.gigya.hasSession === 'function') {
    window.gigya.hasSession().then(
      window.gigya.socialize.getUserInfo({
        callback: (res: any) => {
          if (res.UID != null) {
            sessionStorage.setItem('getUserInfoResponse', JSON.stringify(res))
            store.commit('SET_AUTH_USER', res)
            store.dispatch(AuthActionTypes.GET_USER_INFO, res.UID)
          } else {
            store.dispatch('logout')
            next({ name: 'login' })
          }
        }
      })
    )
  } else {
    console.error('Gigya is not properly initialized.')
    const uid = sessionStorage.getItem('uid')
    if (uid !== null) {
      const getUserInfoResponse = JSON.parse((sessionStorage.getItem('getUserInfoResponse') as string) || '') || null
      if (getUserInfoResponse) {
        store.commit('SET_AUTH_USER', getUserInfoResponse)
        store.dispatch(AuthActionTypes.GET_USER_INFO, uid)
      } else {
        store.dispatch('logout')
        next({ name: 'login' })
      }
    } else {
      store.dispatch('logout')
      next({ name: 'login' })
    }
    // Handle the error or redirect as needed
  }
}

function checkPermission (store: Store<IRootState>, next: NavigationGuardNext, key: string) {
  function proceed () {
    const permissionData = store.state?.main?.permissions
    if (store.getters.getIsFullLoaded() === true) {
      if (store.state.auth.user.allAvailablePermission.includes(key) && getValueByLabel(permissionData, key)) {
        next()
      } else {
        next({ name: 'Error403' })
      }
    }
  }

  if (store.getters.getIsFullLoaded() !== true || !store.state?.main?.permissions.length) {
    if (store.getters.getIsFullLoaded() !== true) {
      // Handle asynchronous initialization here
      checkSession(store, next)

      store.watch(store.getters.getIsFullLoaded, function () {
        if (store.getters.getIsFullLoaded() === true && store.state?.main?.permissions.length) {
          proceed()
        }
      })
    }

    if (!store.state?.main?.permissions.length) {
      store.dispatch('main/FETCH_PERMISSIONS', { root: true })

      store.watch(store.getters['main/getPermissions'], function () {
        if (store.getters.getIsFullLoaded() === true && store.state?.main?.permissions.length) {
          proceed()
        }
      })
    }
  } else {
    proceed()
  }
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createRoutes (store: Store<IRootState>): RouteRecordRaw[] {
  return [
    {
      path: '/',
      name: 'homepage',
      meta: {
        requireAuthentication: false,
        title: 'Strona Główna | ePartnerKP',
        description: 'Platforma jest własnością Kompanii Piwowarskiej SA dedykowaną Partnerom Handlowym. Jeśli chciałbyś rozpocząć współpracę związaną z ePartnerKP, skontaktuj się ze swoim opiekunem w Kompanii Piwowarskiej.',
        layout: DefaultLayout
      },
      component: () => import(/* webpackChunkName: "homepage" */ '../pages/HomePage.vue')
    },
    {
      path: '/verification',
      name: 'userVerification',
      meta: { isUserVerification: true, title: 'Phone Login' },
      component: () => import(/* webpackChunkName: "verification" */ '../pages/UserVerificationPage.vue')
    },
    {
      path: '/verification-phone',
      name: 'userVerificationPhone',
      meta: { isUserVerification: true, title: 'Phone Login' },
      component: () => import(/* webpackChunkName: "verification" */ '../pages/UserVerificationPage.vue')
    },
    {
      path: '/revalidate',
      name: 'userRevalidate',
      meta: { isUserVerification: true, title: 'Phone Login' },
      component: () => import(/* webpackChunkName: "verification" */ '../pages/UserVerificationPage.vue')
    },
    {
      path: '/user-registration-success',
      name: 'userRegistrationSuccess',
      meta: { isuserRegistrationSuccess: true, title: 'ePartnerKP' },
      component: () => import(/* webpackChunkName: "verification" */ '../pages/UserRegistrationSuccess.vue')
    },
    {
      path: '/select-login-view',
      name: 'selectloginview',
      meta: {
        title: 'ePartnerKP',
        noPortalChrome: true
      },
      component: () => import(/* webpackChunkName: "verification" */ '../pages/SelectLoginView.vue')
    },
    {
      path: '/catalog/products',
      name: 'products',
      meta: {
        isProducts: 'product',
        requireAuthentication: true,
        title: 'Products catalogue',
        layout: DefaultLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Catalogue'),
      component: () => import(/* webpackChunkName: "products" */ '../pages/CatalogPage.vue')
    },
    {
      path: '/catalog/quickorder',
      name: 'quickorder',
      meta: {
        isProducts: 'quickorder',
        requireAuthentication: true,
        title: 'Quick Order',
        layout: DefaultLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Catalogue'),
      component: () => import(/* webpackChunkName: "products" */ '../pages/CatalogPage.vue')
    },
    {
      path: '/catalog/savedorder',
      name: 'savedorder',
      meta: {
        isProducts: 'savedorder',
        requireAuthentication: true,
        title: 'Saved Orders',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Catalogue'),
      component: () => import(/* webpackChunkName: "products" */ '../pages/CatalogPage.vue')
    },
    {
      path: '/catalog/specialoffer',
      name: 'specialoffer',
      meta: {
        isProducts: 'specialoffer',
        requireAuthentication: true,
        title: 'Special Offers',
        layout: DefaultLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Catalogue'),
      component: () => import(/* webpackChunkName: "products" */ '../pages/CatalogPage.vue')
    },
    {
      path: '/orders/saved',
      name: 'savedOrders',
      meta: {
        isProducts: 'sarders',
        requireAuthentication: true,
        title: 'Saved Orders',
        layout: HomeLayout,
        tab: 'saved'
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'My Orders'),
      component: () => import('@/components/orders/OrdersPage.vue')
    },
    {
      path: '/orders/pending',
      name: 'pendingOrders',
      meta: {
        isProducts: 'parders',
        requireAuthentication: true,
        title: 'Pending Orders',
        layout: DefaultLayout,
        tab: 'pending'
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'My Orders'),
      component: () => import('@/components/orders/OrdersPage.vue')
    },
    {
      path: '/catalog/:id',
      name: 'productsDetail',
      meta: {
        requireAuthentication: true,
        title: 'Product details',
        layout: DefaultLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Catalogue'),
      component: () => import(/* webpackChunkName: "productsDetail" */ '../components/products/ProductDescription.vue')
    },
    {
      path: '/catalog/favorites',
      name: 'favorites',
      meta: {
        isProducts: 'favorites',
        requireAuthentication: true,
        title: 'My favourites',
        layout: DefaultLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },

      beforeEnter: (to, from, next) => checkPermission(store, next, 'Catalogue'),
      component: () => import(/* webpackChunkName: "favorites" */ '../components/products/FavoritesPage.vue')
    },
    {
      path: '/orders/history',
      name: 'orders-history',
      meta: {
        tab: 'history',
        requireAuthentication: true,
        title: 'Order History',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'My Orders'),
      component: () => import('@/components/orders/OrdersPage.vue')
    },
    {
      path: '/my-profile/details',
      name: 'details',
      meta: {
        tab: 'details',
        requireAuthentication: true,
        title: 'My profile',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'My Profile'),
      component: () => import(/* webpackChunkName: "profile" */ '../pages/MyAccount.vue')
    },
    {
      path: '/my-profile/management',
      name: 'management',
      meta: {
        tab: 'management',
        requireAuthentication: true,
        title: 'Management',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'My Profile'),
      component: () => import(/* webpackChunkName: "profile" */ '../pages/MyAccount.vue')
    },
    {
      path: '/my-profile/company',
      name: 'company',
      meta: {
        tab: 'company',
        requireAuthentication: true,
        title: 'Company',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'My Profile'),
      component: () => import(/* webpackChunkName: "profile" */ '../pages/MyAccount.vue')
    },
    {
      path: '/notifications',
      name: 'notification',
      meta: {
        tab: 'notifications',
        requireAuthentication: true,
        title: 'Notifications',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'My Profile'),
      component: () => import(/* webpackChunkName: "profile" */ '../pages/MyAccount.vue')
    },
    {
      path: '/news',
      name: 'news',
      meta: {
        requireAuthentication: false,
        title: 'ePartnerKP',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }],
        tabSelection: 'news'
      },
      component: () => import(/* webpackChunkName: "news" */ '../pages/FilesPage.vue')
    },
    {
      path: '/allbrands',
      name: 'brands',
      meta: {
        requireAuthentication: false,
        title: 'Our Brands',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },

      component: () => import(/* webpackChunkName: "news" */ '../pages/BrandsPage.vue')
    },
    {
      path: '/brands/:path',
      name: 'newbrands',
      meta: {
        requireAuthentication: false,
        title: 'New Brands',
        layout: HomeLayout,
        breadCrumb: [
          { text: 'Home', path: '/' },
          { text: 'Our brands', path: '/allbrands' }
        ]
      },
      /*
      beforeEnter: (to, from, next) => {
        if (constants.application.theme === 'prazdroj') {
          return checkPermission(store, next, 'News & Events')
        } else {
          return next()
        }
      }, */
      component: () => import(/* webpackChunkName: "news" */ '../pages/BrandsFilterPage.vue')
    },
    {
      path: '/contracts',
      name: 'contracts',
      meta: {
        requireAuthentication: true,
        title: 'Contracts',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Contracts'),
      component: () => import('../pages/MyAccount.vue')
    },
    {
      path: '/loyalty',
      name: 'loyalty',
      meta: {
        requireAuthentication: true,
        title: 'Loyalty',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      // beforeEnter: (to, from, next) => checkPermission(store, next, 'Loyalty'),
      component: () => import('../pages/LoyaltyPage.vue')
    },
    {
      path: '/reporting',
      name: 'reporting',
      meta: {
        requireAuthentication: true,
        title: 'Reporting',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Reporting'),
      component: () => import('../pages/MyAccount.vue')
    },
    {
      path: '/clp',
      name: 'centralLoginPage',
      meta: {
        noPortalChrome: true,
        title: window.gigya?.getUrlParam('startingPortal') === 'BC' ? window.gigya?.getUrlParam('loginMethod') === 'phone' ? 'Phone LoginBC' : 'BCcentralLoginPage' : window.gigya?.getUrlParam('loginMethod') === 'phone' ? 'Phone Login' : 'centralLoginPage'
      },
      beforeEnter: (to, from, next) => {
        const method = to.query.gig_loginMethod || (window as any).gigya?.getUrlParam('loginMethod')
        const portal = to.query.gig_startingPortal || (window as any).gigya?.getUrlParam('startingPortal')
        document.title = portal === 'BC' ? method === 'phone' ? 'Phone LoginBC' : 'BCcentralLoginPage' : method === 'phone' ? 'Phone Login' : 'centralLoginPage'
        next()
      },
      component: () => import('../pages/CentralLoginPage.vue')
    },
    {
      path: '/ssoLogin',
      name: 'ssoLoginPage',
      meta: {
        requireAuthentication: true,
        title: 'SsoLogin',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      component: () => import('../pages/ssoLogin.vue')
    },
    {
      path: '/my-documents',
      name: 'myDocuments',
      meta: {
        tab: 'my-document',
        requireAuthentication: true,
        title: 'My documents',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'My Documents'),
      component: () => import(/* webpackChunkName: "favorites" */ '../pages/MyAccount.vue')
    },
    {
      path: '/marketing-materials',
      name: 'marketingMaterials',
      meta: {
        requireAuthentication: true,
        title: 'Marketing Materials',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }],
        tabSelection: 'marketing'
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Marketing Materials'),
      component: () => import(/* webpackChunkName: "favorites" */ '../pages/FilesPage.vue')
    },
    {
      path: '/news',
      name: 'news',
      meta: {
        requireAuthentication: false,
        title: 'News',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }],
        tabSelection: 'news'
      },
      component: () => import(/* webpackChunkName: "news" */ '../pages/FilesPage.vue')
    },
    {
      path: '/training',
      name: 'training',
      meta: { requireAuthentication: true, title: 'Training', layout: HomeLayout },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Training & Learning'),
      component: () => import(/* webpackChunkName: "news" */ '../pages/TrainingLearningPage.vue')
    },
    {
      path: '/news/:path',
      name: 'newsItem',
      meta: {
        requireAuthentication: false,
        title: 'News',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }],
        tabSelection: 'news'
      },
      /*
      beforeEnter: (to, from, next) => {
        if (constants.application.theme === 'prazdroj') {
          return checkPermission(store, next, 'News & Events')
        } else {
          return next()
        }
      }, */
      component: () => import(/* webpackChunkName: "news" */ '../pages/ArticlePage.vue')
    },
    {
      path: '/support/:path',
      name: 'support',
      meta: {
        requireAuthentication: true,
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => {
        checkPermission(store, next, 'Support')
        let dynamicTitle = String(to.params.path)
        switch (String(to.params.path)) {
          case 'other-requests':
            dynamicTitle = 'Other Requestes'
            break
          case 'knowledge-base':
            dynamicTitle = 'Knowledge Base'
            break
          case 'faq':
            dynamicTitle = 'FaQ'
            break
          case 'contacts':
            dynamicTitle = 'Contacts'
            break
        }
        if (dynamicTitle) {
          document.title = dynamicTitle
        }
      },
      component: () => import(/* webpackChunkName: "favorites" */ '../pages/AsahiSupportPage.vue')
    },
    {
      path: '/login',
      name: 'login',
      meta: { noPortalChrome: true, title: 'Logowanie | ePartnerKP', description: 'Zaloguj się do platformy ePartnerKP, która jest własnością Kompanii Piwowarskiej SA dedykowaną Partnerom Handlowym.' }, // noPortalChrome: true if don't want to display side menu & main header e.g. login page
      component: () => import(/* webpackChunkName: "login" */ '../pages/LoginPage.vue')
      // component: () => import(/* webpackChunkName: "login" */ '../pages/LoginMethodSelection.vue')
    },
    {
      path: '/login-phone',
      name: 'login-phone',
      meta: {
        noPortalChrome: true,
        title: 'Phone Login',
        breadCrumb: [{ text: '', path: '/login' }]
      }, // noPortalChrome: true if don't want to display side menu & main header e.g. login page
      component: () => import(/* webpackChunkName: "login" */ '../pages/LoginPhonePage.vue')
    },
    {
      path: '/forgot-password',
      name: 'forgotPassword',
      meta: { title: 'Forgot Password' },
      component: () => import(/* webpackChunkName: "forgot password" */ '../pages/ForgotPasswordPage.vue')
    },
    {
      path: '/reset-password',
      name: 'resetPassword',
      meta: { title: 'Reset Password' },
      component: () => import(/* webpackChunkName: "reset password" */ '../pages/ResetPasswordPage.vue')
    },
    {
      path: '/cart',
      name: 'cart',
      meta: {
        hideFooter: true,
        requireAuthentication: true,
        title: 'Cart',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Catalogue'),
      component: () => import(/* webpackChunkName: "cart" */ '../pages/CartCheckoutPage.vue')
    },
    {
      path: '/checkoutpayment',
      name: 'paymentgateway',
      meta: {
        hideFooter: true,
        requireAuthentication: true,
        title: 'ePartnerKP',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      beforeEnter: (to, from, next) => checkPermission(store, next, 'Catalogue'),
      component: () => import(/* webpackChunkName: "cart" */ '../pages/CartPaymentPage.vue')
    },
    {
      path: '/foodparing',
      name: 'foodParing',
      meta: {
        requireAuthentication: true,
        title: 'Food Paring',
        layout: HomeLayout
        // breadCrumb: [{ text: 'Home', path: '/' }]
      },
      component: () => import(/* webpackChunkName: "favorites" */ '../pages/FoodParing.vue')
    },
    {
      path: '/foodparing/configurator',
      name: 'foodParingConfigurator',
      meta: {
        requireAuthentication: true,
        title: 'Food Paring',
        layout: HomeLayout
      },
      component: () => import(/* webpackChunkName: "favorites" */ '../pages/FoodParingConfigurator.vue')
    },
    {
      path: '/piwopedia',
      name: 'piwopedia',
      meta: {
        requireAuthentication: true,
        title: 'Piwopedia',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      component: () => import(/* webpackChunkName: "favorites" */ '../pages/PiwopediaPage.vue')
    },
    {
      path: '/distributor-map',
      name: 'distributorMap',
      meta: {
        requireAuthentication: true,
        title: 'Distributor Map',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      component: () => import(/* webpackChunkName: "favorites" */ '../pages/DistributorMap.vue')
    },
    {
      path: '/thanks',
      name: 'thanks',
      meta: { requireAuthentication: true, title: 'Thanks', layout: HomeLayout },
      component: () => import(/* webpackChunkName: "thanks" */ '../components/orders/ThanksPage.vue')
    },
    {
      path: '/ko',
      name: 'ko',
      meta: { requireAuthentication: true, error: true, title: 'ePartnerKP', layout: HomeLayout },
      component: () => import(/* webpackChunkName: "ko" */ '../pages/ThanksPage.vue')
    },
    {
      path: '/contact',
      name: 'contact',
      meta: {
        title: 'Kontakt | ePartnerKP',
        description: 'Platforma jest własnością Kompanii Piwowarskiej SA dedykowaną Partnerom Handlowym. Jeśli chcesz nawiązać z nami współpracę, skontaktuj się poprzez sprzedaz@kp.pl lub zwróć się do swojego opiekuna w KP.',
        layout: HomeLayout,
        breadCrumb: [{ text: 'Home', path: '/' }]
      },
      component: () => import(/* webpackChunkName: "contact" */ '../pages/ContactPage.vue')
    },
    {
      path: '/saml-error',
      name: 'saml-error',
      meta: { noPortalChrome: true, title: 'ePartnerKP' }, // noPortalChrome: true if don't want to display side menu & main header e.g. login page
      component: () => import(/* webpackChunkName: "files" */ '../pages/ErrorSAMLPage.vue')
    },
    {
      path: '/error403',
      name: 'Error403',
      meta: { title: 'ePartnerKP' },
      component: () => import(/* webpackChunkName: "error403" */ '../pages/Error403Page.vue')
    },

    // this MUST be the last route
    {
      path: '/:pathMatch(.*)*',
      name: 'Error404',
      meta: { title: 'ePartnerKP' },
      component: () => import(/* webpackChunkName: "error404" */ '../pages/Error404Page.vue')
    }
  ]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function createRouter (store: Store<IRootState>): AsahiRouter {
  const routes = createRoutes(store)

  const router = createAsahiRouter({
    routes,
    history,
    scrollBehavior (to, from, savedPosition) {
      if (savedPosition && from.name !== 'newsItem') {
        return savedPosition
      }

      if (to.query.c && from.params.productUrl === to.params.productUrl) {
        return {}
      }

      if (
        to.name === 'products' &&
        from.name === 'products' &&
        to.path === from.path &&
        to.query.page === from.query.page
      ) {
        return {}
      }

      if (to.hash) {
        return { el: to.hash }
      }

      return { top: 0 }
    }
  }) as AsahiRouter

  // https://next.router.vuejs.org/guide/advanced/navigation-guards.html#optional-third-argument-next
  router.beforeEach(async (to, from) => {
    const isToClp = to.path === '/clp'
    const isFromClp = from.path === '/clp'

    const isFirstLoad = !gigyaInitialized

    const isModeSwitch = isToClp !== isFromClp

    if (isFirstLoad || isModeSwitch) {
      const mode: 'default' | 'clp' = isToClp ? 'clp' : 'default'

      if (currentGigyaMode !== mode) {
        const oldScript = document.getElementById('gigya-script')
        if (oldScript) {
          oldScript.remove()
        }

        const script = document.createElement('script')
        script.id = 'gigya-script'
        script.type = 'module'

        const apiKey =
        mode === 'clp'
          ? process.env.VUE_APP_API_KEY_CLP
          : process.env.VUE_APP_API_KEY

        script.textContent = `
        import GlobalObject from '/global.js';
        GlobalObject.loadGigyaScript('${apiKey}');
      `

        document.head.appendChild(script)

        gigyaInitialized = true
        currentGigyaMode = mode
      }
    }

    const DEFAULT_TITLE = constants.application.pageTitle
    document.title = (to.meta.title as string) || DEFAULT_TITLE
    const metaDescEl = document.querySelector('meta[name="description"]')
    if (metaDescEl) {
      metaDescEl.setAttribute('content', (to.meta.description as string) || '')
    }

    store.commit('main/SET_ROUTE', to)

    if (store.state.main.ssrRendered && store.state.main.pageNotFound) {
      store.commit('main/SET_SSR_RENDERED', false)
    }
    store.commit('main/SET_PAGE_NOT_FOUND', true)

    return true
  })

  router.beforeEach((to, from, next) => {
    if (to.name === 'centralLoginPage') {
      const fromBC = from.query?.gig_startingPortal === 'BC'
      const portal = window.gigya?.getUrlParam('startingPortal')
      const method = window.gigya?.getUrlParam('loginMethod')
      if (portal === 'BC') {
        document.title = method === 'phone' ? 'Phone LoginBC' : 'BCcentralLoginPage'
      } else {
        document.title = method === 'phone' ? !fromBC ? 'Phone Login' : 'Phone LoginBC' : 'centralLoginPage'
      }
    }
    next()
  })

  router.afterEach(async (to, from) => {
    store.commit('main/SET_ROUTE_LOADING', false)
    store.commit('main/SET_HISTORY', from)
    return true
  })

  router.navigate = (url: string | CustomEvent) => {
    const target =
      typeof url === 'string'
        ? new URL(url, window.location.toString())
        : new URL(url.detail?.href ? url.detail.href : url.detail, window.location.toString())
    if (
      target.origin === window.location.origin &&
      target.pathname === window.location.pathname &&
      target.search === window.location.search
    ) {
      if (target.hash === '') return
      const element = document.querySelector(target.hash) as HTMLElement
      window.scrollBy({
        top: element ? element.getBoundingClientRect().top : 0,
        left: 0,
        behavior: 'smooth'
      })
    } else if (target.origin === window.location.origin) {
      router.push(target.pathname + target.search + target.hash)
    } else {
      window.location.assign(target.href)
    }
  }

  return router
}
